/***
*
*   EVENT/DETAIL
*   View the details of an individual event
*
**********/

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Breadcrumb, Typography, Button, Tag, Empty
} from 'antd';
import {
  ThunderboltOutlined, HomeOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { Animate, Loader, useAPI } from 'components/lib';

const { Title, Text } = Typography;

export function EventDetail() {
  const navigate = useNavigate();
  const { name: group, id } = useParams();

  const event = useAPI(`/api/event/${id}`);
  const [eventData, setEventData] = useState(null);

  useEffect(() => {
    if (event.data?.length) {
      const data = event.data[0];
      
      let time = data.time.split('T');
      time = time[0] + ' ' + time[1].split('.')[0];

      const formatted = {
        ...data,
        time,
        metadata: data.metadata ? JSON.stringify(data.metadata, null, 2) : 'N/A'
      };

      setEventData(formatted);
    }
  }, [event.data]);

  if (event.loading) {
    return <Loader />;
  }

  if (!eventData) {
    return (
      <Card>
        <Empty description="Event not found">
          <Button type="primary" onClick={() => navigate('/events')}>
            Back to Events
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <Animate type="pop">
      <div>
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/dashboard"><HomeOutlined /> Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item href="/events">Event Groups</Breadcrumb.Item>
          <Breadcrumb.Item href={`/events/${group}`}>{group}</Breadcrumb.Item>
          <Breadcrumb.Item>Detail</Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              Event Details
            </Title>
            <Text type="secondary">ID: {eventData.id}</Text>
          </div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/events/${group}`)}>
            Back to Events
          </Button>
        </div>

        {/* Event Information */}
        <Card title={<Text strong>Event Information</Text>}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Event Name">
              <Tag color="purple">{eventData.name}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Event ID">
              <Text code copyable>{eventData.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="User Name">
              {eventData.user_name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="User Email">
              <Text copyable>{eventData.user_email || 'N/A'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Timestamp">
              <Tag color="blue">{eventData.time}</Tag>
            </Descriptions.Item>
            {eventData.workspace_id && (
              <Descriptions.Item label="Workspace ID">
                <Text code copyable>{eventData.workspace_id}</Text>
              </Descriptions.Item>
            )}
            {eventData.metadata && eventData.metadata !== 'N/A' && (
              <Descriptions.Item label="Metadata">
                <pre style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: 16, 
                  borderRadius: 4,
                  maxHeight: 400,
                  overflow: 'auto',
                  fontSize: 12
                }}>
                  {eventData.metadata}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      </div>
    </Animate>
  );
}